import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import PublicReveal from './PublicReveal';
import { resolveTrackCoverImage } from '../../utils/trackCoverImage';
import type { PublicFiliereTrack } from '../../utils/publicFilieres';
import '../../styles/publicSectionWave.css';
import '../../styles/publicFiliereTrackPage.css';

type Props = {
  track: PublicFiliereTrack;
};

const HIGHLIGHTS = [
  {
    icon: 'verified',
    title: 'Parcours certifiants',
    body: 'Des formations structurées avec évaluations, vacations planifiées et délivrance de brevet à l’issue du parcours.',
  },
  {
    icon: 'groups',
    title: 'Cohortes à Kinshasa',
    body: 'Rejoignez une promotion en présentiel ou hybride, avec un calendrier clair et un suivi pédagogique continu.',
  },
] as const;

export default function PublicFiliereTrackAbout({ track }: Props) {
  const parcoursCount = track.filieres.length;
  const image = resolveTrackCoverImage(track);

  return (
    <section className="public-track-about public-section-before-programme-wave relative scroll-mt-24 overflow-x-clip pt-16 sm:pt-24">
      <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20">
        <div className="relative order-2 flex w-full justify-center lg:order-1">
          <div className="absolute -left-8 -top-8 -z-10 h-56 w-56 rounded-full bg-secondary-container/30 blur-2xl" aria-hidden />
          <div
            className="public-track-organic-shape absolute -bottom-8 right-0 -z-10 h-36 w-72 rotate-12 bg-primary/5"
            aria-hidden
          />

          <div className="public-track-about-visual">
            <div className="public-track-about-stage relative">
              <PublicReveal animation="fade-in-left" className="public-track-about-reveal">
                <div className="public-track-about-frame">
                  <div className="public-track-organic-shape public-track-about-frame-surface public-track-soft-bloom h-full w-full overflow-hidden border-[16px] border-white bg-white">
                    <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                  </div>
                </div>
              </PublicReveal>

              <div className="public-track-about-badge flex h-28 w-28 items-center justify-center rounded-full bg-primary text-white">
                <div className="text-center">
                  <p className="text-2xl font-extrabold">{parcoursCount}+</p>
                  <p className="text-[10px] font-bold uppercase leading-none tracking-widest">Formations</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PublicReveal animation="fade-in-right" className="order-1 space-y-6 lg:order-2">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">Ce domaine</p>
          <h2 className="text-3xl font-bold leading-tight text-on-background sm:text-4xl">
            Des compétences concrètes pour le marché de l&apos;emploi.
          </h2>
          {track.filieres.length > 0 ? (
            <ul className="list-none space-y-2 pb-2 text-base leading-relaxed text-on-surface-variant">
              {track.filieres.map((filiere) => (
                <li key={filiere.id} className="flex items-start gap-2.5">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                  <span>{filiere.libelle}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="grid grid-cols-1 gap-4">
            {HIGHLIGHTS.map((item) => (
              <div
                key={item.title}
                className="group flex items-start gap-4 rounded-public-card p-4 transition-all hover:bg-white hover:shadow-lg"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                  <LucideSymbolIcon name={item.icon} tone="inherit" size={24} className="text-current" />
                </div>
                <div>
                  <h3 className="mb-1 text-lg font-bold text-on-background">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-on-surface-variant">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </PublicReveal>
      </div>
    </section>
  );
}
